import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  setDoc,
  onSnapshot,
  query,
  orderBy,
  where,
  serverTimestamp,
  increment,
} from 'firebase/firestore';
import { db, auth } from './firebase';
import { sanitiseString } from '@/utils/validators';

const COLLECTION = 'todos';

function toTodo(snap) {
  const d = snap.data();
  return {
    id:                snap.id,
    text:              d.text,
    targetDate:        d.targetDate,
    createdBy:         d.createdBy,
    assignedTo:        d.assignedTo,
    status:            d.status ?? 'pending',   // 'pending' | 'done'
    completedAt:       d.completedAt?.toDate?.()?.toISOString() ?? null,
    completionComment: d.completionComment ?? null,
    createdAt:         d.createdAt?.toDate?.()?.toISOString() ?? null,
  };
}

/** Add a new todo item */
export async function addTodo(todo) {
  const user = auth.currentUser;
  const ref = await addDoc(collection(db, COLLECTION), {
    text:        sanitiseString(todo.text),
    targetDate:  todo.targetDate,
    createdBy:   user?.email ?? 'unknown',
    assignedTo:  todo.assignedTo,
    status:      'pending',
    createdAt:   serverTimestamp(),
  });
  // Notify the assignee (skip if self-assigned)
  await notifyTodoAssigned(todo.assignedTo);
  return ref.id;
}

/** Update a todo */
export async function updateTodo(id, fields) {
  await updateDoc(doc(db, COLLECTION, id), {
    ...fields,
    updatedAt: serverTimestamp(),
  });
  // Notify if assignee changed
  if (fields.assignedTo) {
    await notifyTodoAssigned(fields.assignedTo);
  }
}

/** Mark a todo as done with a mandatory completion comment */
export async function completeTodo(id, comment) {
  await updateDoc(doc(db, COLLECTION, id), {
    status:            'done',
    completedAt:       serverTimestamp(),
    completionComment: sanitiseString(comment),
    updatedAt:         serverTimestamp(),
  });
}

/** Reopen a completed todo */
export async function reopenTodo(id) {
  await updateDoc(doc(db, COLLECTION, id), {
    status:      'pending',
    completedAt: null,
    updatedAt:   serverTimestamp(),
  });
}

/** Delete a todo */
export async function deleteTodo(id) {
  await deleteDoc(doc(db, COLLECTION, id));
}

/** Get all todos */
export async function getTodos() {
  const snap = await getDocs(
    query(collection(db, COLLECTION), orderBy('createdAt', 'desc'))
  );
  return snap.docs.map(toTodo);
}

/**
 * Delete todos that were completed more than 7 days ago.
 * Called automatically when the screen loads.
 */
export async function pruneOldCompleted() {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - 7);
  const cutoffISO = cutoff.toISOString();

  const snap = await getDocs(
    query(collection(db, COLLECTION), where('status', '==', 'done'))
  );

  const toDelete = snap.docs.filter(d => {
    const completedAt = d.data().completedAt?.toDate?.()?.toISOString();
    return completedAt && completedAt < cutoffISO;
  });

  await Promise.all(toDelete.map(d => deleteDoc(doc(db, COLLECTION, d.id))));
  return toDelete.length;
}

// ─── In-app notifications ────────────────────────────────────────────────────

const NOTIF_COLLECTION = 'todo_notifications';

/**
 * Increment unread count for a user when a todo is assigned to them.
 * Skip if the creator is the same as the assignee (no self-notification).
 */
export async function notifyTodoAssigned(assignedToEmail) {
  const creatorEmail = auth.currentUser?.email;
  if (!assignedToEmail || assignedToEmail === creatorEmail) return;

  const ref = doc(db, NOTIF_COLLECTION, assignedToEmail);
  await setDoc(ref, { count: increment(1), updatedAt: serverTimestamp() }, { merge: true });
}

/** Reset unread count to 0 for the current user */
export async function clearTodoNotifications() {
  const email = auth.currentUser?.email;
  if (!email) return;
  const ref = doc(db, NOTIF_COLLECTION, email);
  await setDoc(ref, { count: 0, updatedAt: serverTimestamp() }, { merge: true });
}

/**
 * Subscribe to the current user's unread notification count in real time.
 * @param {string} email
 * @param {(count: number) => void} callback
 * @returns unsubscribe function
 */
export function subscribeTodoNotifications(email, callback) {
  if (!email) return () => {};
  const ref = doc(db, NOTIF_COLLECTION, email);
  return onSnapshot(ref, (snap) => {
    callback(snap.exists() ? (snap.data().count ?? 0) : 0);
  }, () => callback(0));
}
